import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, InputNumber, Select, message, Space, Popconfirm, Tag, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CreditCardOutlined, FileTextOutlined, SettingOutlined, ShopOutlined } from '@ant-design/icons';
import axios from '../../utils/axiosConfig';
import './BillingSubscriptions.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

function BillingSubscriptions() {
  const [activeTab, setActiveTab] = useState('agency-plans');
  const [agencyPlans, setAgencyPlans] = useState([]);
  const [clientPlans, setClientPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchAgencyPlans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/billing/agency-plans');
      setAgencyPlans(data.plans || []);
    } catch (error) {
      message.error('Failed to fetch agency plans: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClientPlans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/billing/client-plans');
      setClientPlans(data.plans || []);
    } catch (error) {
      message.error('Failed to fetch client plans: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/billing/invoices');
      setInvoices(data.invoices || []);
    } catch (error) {
      message.error('Failed to fetch invoices: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/billing/payment-settings');
      setPaymentSettings(data.settings || {});
      form.setFieldsValue(data.settings || {});
    } catch (error) {
      message.error('Failed to fetch payment settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (activeTab === 'agency-plans') {
      fetchAgencyPlans();
    } else if (activeTab === 'client-plans') {
      fetchClientPlans();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'payment-settings') {
      fetchPaymentSettings();
    }
  }, [activeTab, fetchAgencyPlans, fetchClientPlans, fetchInvoices, fetchPaymentSettings]);

  const openModal = (type, record = null) => {
    setModalType(type);
    setEditingRecord(record);
    setModalVisible(true);
    
    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      if (modalType === 'agency-plan') {
        if (editingRecord) {
          await axios.put(`/api/admin/billing/agency-plans/${editingRecord.id}`, values);
          message.success('Agency plan updated successfully');
        } else {
          await axios.post('/api/admin/billing/agency-plans', values);
          message.success('Agency plan created successfully');
        }
        fetchAgencyPlans();
      } else if (modalType === 'client-plan') {
        if (editingRecord) {
          await axios.put(`/api/admin/billing/client-plans/${editingRecord.id}`, values);
          message.success('Client plan updated successfully');
        } else {
          await axios.post('/api/admin/billing/client-plans', values);
          message.success('Client plan created successfully');
        }
        fetchClientPlans();
      }
      closeModal();
    } catch (error) {
      message.error('Operation failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === 'agency-plan') {
        await axios.delete(`/api/admin/billing/agency-plans/${id}`);
        message.success('Agency plan deleted successfully');
        fetchAgencyPlans();
      } else if (type === 'client-plan') {
        await axios.delete(`/api/admin/billing/client-plans/${id}`);
        message.success('Client plan deleted successfully');
        fetchClientPlans();
      }
    } catch (error) {
      message.error('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handlePaymentSettingsUpdate = async (values) => {
    try {
      await axios.put('/api/admin/billing/payment-settings', values);
      message.success('Payment settings updated successfully');
      fetchPaymentSettings();
    } catch (error) {
      message.error('Update failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const planColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `$${parseFloat(price).toFixed(2)}`,
    },
    {
      title: 'Billing Cycle',
      dataIndex: 'billing_cycle',
      key: 'billing_cycle',
      render: (cycle) => <Tag color="blue">{cycle?.toUpperCase()}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal(activeTab === 'agency-plans' ? 'agency-plan' : 'client-plan', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this plan?"
            onConfirm={() => handleDelete(activeTab === 'agency-plans' ? 'agency-plan' : 'client-plan', record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const invoiceColumns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `$${parseFloat(amount).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'paid' ? 'green' : status === 'pending' ? 'orange' : 'red';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link">View</Button>
          <Button type="link">Download</Button>
        </Space>
      ),
    },
  ];

  const renderPlanForm = () => (
    <>
      <Form.Item name="name" label="Plan Name" rules={[{ required: true }]}>
        <Input placeholder="e.g., Professional Plan" />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <TextArea rows={3} placeholder="Plan description" />
      </Form.Item>
      <Form.Item name="price" label="Price" rules={[{ required: true }]}>
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: '100%' }}
          prefix="$"
        />
      </Form.Item>
      <Form.Item name="billing_cycle" label="Billing Cycle" rules={[{ required: true }]}>
        <Select>
          <Option value="monthly">Monthly</Option>
          <Option value="yearly">Yearly</Option>
          <Option value="quarterly">Quarterly</Option>
        </Select>
      </Form.Item>
      <Form.Item name="features" label="Features (JSON)">
        <TextArea rows={4} placeholder='{"feature1": true, "feature2": "value"}' />
      </Form.Item>
      <Form.Item name="is_active" label="Active" valuePropName="checked">
        <Switch />
      </Form.Item>
    </>
  );

  return (
    <div className="billing-subscriptions-container">
      <div className="page-header">
        <h1>Billing & Subscriptions</h1>
        <p>Manage subscription plans, invoices, and payment settings</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="billing-tabs">
        <TabPane
          tab={
            <span>
              <ShopOutlined />
              Enterprise Agency Plans
            </span>
          }
          key="agency-plans"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>Enterprise Agency Subscription Plans</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('agency-plan')}
              >
                Add Plan
              </Button>
            </div>
            <Table
              columns={planColumns}
              dataSource={agencyPlans}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <CreditCardOutlined />
              Client Plans
            </span>
          }
          key="client-plans"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>Client Subscription Plans</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('client-plan')}
              >
                Add Plan
              </Button>
            </div>
            <Table
              columns={planColumns}
              dataSource={clientPlans}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Invoices & Payments
            </span>
          }
          key="invoices"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>All Invoices</h2>
            </div>
            <Table
              columns={invoiceColumns}
              dataSource={invoices}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SettingOutlined />
              Payment Gateway Settings
            </span>
          }
          key="payment-settings"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>Payment Gateway Configuration</h2>
            </div>
            <Form
              form={form}
              layout="vertical"
              onFinish={handlePaymentSettingsUpdate}
              initialValues={paymentSettings}
            >
              <Form.Item name="stripe_enabled" label="Stripe Enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="stripe_public_key" label="Stripe Public Key">
                <Input placeholder="pk_..." />
              </Form.Item>
              <Form.Item name="stripe_secret_key" label="Stripe Secret Key">
                <Input.Password placeholder="sk_..." />
              </Form.Item>
              <Form.Item name="paypal_enabled" label="PayPal Enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="paypal_client_id" label="PayPal Client ID">
                <Input />
              </Form.Item>
              <Form.Item name="paypal_secret" label="PayPal Secret">
                <Input.Password />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Save Settings
                </Button>
              </Form.Item>
            </Form>
          </div>
        </TabPane>
      </Tabs>

      <Modal
        title={`${editingRecord ? 'Edit' : 'Add'} ${modalType === 'agency-plan' ? 'Agency' : 'Client'} Plan`}
        open={modalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {renderPlanForm()}
        </Form>
      </Modal>
    </div>
  );
}

export default BillingSubscriptions;
